import { push } from 'connected-react-router';
import { catchError, concatAll, filter, map, mergeMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import i18next from 'i18next';
import { PayloadAction } from '@reduxjs/toolkit';
import { showNotification, showNotificationFromError } from 'components/app/store/UserSlice';
import RootEpic from 'store/RootEpic';
import {
  ListMsEntraIDOptions,
  deleteMsEntraID,
  getMsEntraID,
  listMsEntraID,
  updateMsEntraID,
  createMsEntraID,
} from 'gql/MsEntraID';
import { PermissionAction, PermissionResource, hasPermission } from 'gql/Permission';
import { CONSOLE_ROUTE, MS_ENTRA_ID_ROUTE } from 'Route';
import Helper from 'utils/Helper';
import MsEntraIDSlice from './MsEntraIDSlice';

// GET
export const getMsEntraIDEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.getMsEntraIDbyID.match),
    mergeMap(({ payload }) => {
      return from(getMsEntraID(payload)).pipe(
        map((response) => {
          return MsEntraIDSlice.actions.getMsEntraIDbyIDSucceeded(response);
        }),
        catchError((error) => {
          return of(MsEntraIDSlice.actions.getMsEntraIDbyIDFailed(error));
        })
      );
    })
  );

export const getMsEntraIDFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.getMsEntraIDbyIDFailed.match),
    map(({ payload }) => showNotificationFromError({ msg: i18next.t('FAILED_GET_ENTRA_ID'), err: payload }))
  );

// LIST
export const listMsEntraIDEpic: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(() =>
      hasPermission(state$.value.user.user?.permissions, PermissionAction.READ, PermissionResource.MS_ENTRA_ID)
    ),
    filter(
      (action): action is PayloadAction<ListMsEntraIDOptions> =>
        MsEntraIDSlice.actions.listMsEntraIDs.match(action) ||
        MsEntraIDSlice.actions.listNextPageMsEntraIDs.match(action)
    ),
    mergeMap(({ payload }) => {
      return from(listMsEntraID(payload)).pipe(
        map((response) => {
          return response.nextToken
            ? [
                MsEntraIDSlice.actions.listMsEntraIDsSucceeded(response.items),
                MsEntraIDSlice.actions.listNextPageMsEntraIDs({
                  includeDeleted: payload.includeDeleted,
                  nextToken: response.nextToken,
                  size: payload.size,
                }),
              ]
            : [MsEntraIDSlice.actions.listMsEntraIDsSucceeded(response.items)];
        }),
        concatAll(),
        catchError((error) => {
          return of(MsEntraIDSlice.actions.listMsEntraIDsFailed(error));
        })
      );
    })
  );
};

export const listMsEntraIDFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.listMsEntraIDsFailed.match),
    map(({ payload }) => showNotificationFromError({ msg: i18next.t('FAILED_LIST_ENTRA_ID'), err: payload }))
  );

// CREATE
export const createMsEntraIDEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.createMsEntraID.match),
    mergeMap(({ payload }) =>
      from(createMsEntraID(payload)).pipe(
        map((response) => MsEntraIDSlice.actions.createMsEntraIDSucceeded(response)),
        catchError((error) => of(MsEntraIDSlice.actions.createMsEntraIDFailed(error)))
      )
    )
  );

export const createMsEntraIDSuccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.createMsEntraIDSucceeded.match),
    mergeMap((action) => {
      return [
        showNotification({ type: 'success', contents: i18next.t('SUCCESS_CREATED_MS_ENTRA_ID', action.payload.id) }),
        push(Helper.interpolateRoute(MS_ENTRA_ID_ROUTE.url, { msEntraIDId: action.payload.id })),
      ];
    })
  );

export const createMsEntraIDFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.createMsEntraIDFailed.match),
    map(({ payload }) => showNotificationFromError({ msg: i18next.t('FAILED_CREATE_MS_ENTRA_ID'), err: payload }))
  );

// UPDATE
export const updateMsEntraIDEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.updateMsEntraID.match),
    mergeMap(({ payload }) => {
      return from(updateMsEntraID(payload)).pipe(
        map((response) => MsEntraIDSlice.actions.updateMsEntraIDSucceeded(response)),
        catchError((error) => of(MsEntraIDSlice.actions.updateMsEntraIDFailed(error)))
      );
    })
  );

export const updateMsEntraIDSuccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.updateMsEntraIDSucceeded.match),
    mergeMap((action) => {
      return [
        showNotification({ type: 'success', contents: i18next.t('SUCCESS_UPDATED_MS_ENTRA_ID', action.payload.id) }),
        push(Helper.interpolateRoute(MS_ENTRA_ID_ROUTE.url, { msEntraIDId: action.payload.id })),
      ];
    })
  );

export const updateMsEntraIDFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.updateMsEntraIDFailed.match),
    map(({ payload }) => {
      return showNotificationFromError({ msg: i18next.t('FAILED_UPDATE_MS_ENTRA_ID'), err: payload });
    })
  );

// DELETE
export const deleteMsEntraIDEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.deleteMsEntraID.match),
    mergeMap(({ payload }) => {
      return from(deleteMsEntraID(payload)).pipe(
        map((response) => MsEntraIDSlice.actions.deleteMsEntraIDSucceeded(response)),
        catchError((error) => of(MsEntraIDSlice.actions.deleteMsEntraIDFailed(error)))
      );
    })
  );

export const deleteMsEntraIDSuccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.deleteMsEntraIDSucceeded.match),
    mergeMap((action) => {
      return [
        showNotification({ type: 'success', contents: i18next.t('SUCCESS_DELETED_MS_ENTRA_ID', action.payload.id) }),
        push(`${CONSOLE_ROUTE.url}?tab=msEntraID`),
      ];
    })
  );

export const deleteMsEntraIDFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDSlice.actions.deleteMsEntraIDFailed.match),
    map(({ payload }) => {
      return showNotificationFromError({ msg: i18next.t('FAILED_DELETE_MS_ENTRA_ID'), err: payload });
    })
  );

const MsEntraIDEpics: RootEpic[] = [
  getMsEntraIDEpic,
  getMsEntraIDFailedEpic,
  listMsEntraIDEpic,
  listMsEntraIDFailedEpic,
  createMsEntraIDEpic,
  createMsEntraIDSuccessEpic,
  createMsEntraIDFailedEpic,
  updateMsEntraIDEpic,
  updateMsEntraIDSuccessEpic,
  updateMsEntraIDFailedEpic,
  deleteMsEntraIDEpic,
  deleteMsEntraIDSuccessEpic,
  deleteMsEntraIDFailedEpic,
];

export default MsEntraIDEpics;
